import { normalizeInternalUrl } from "@/lib/site/trailing-slash";

export const SITE_HEADER = "x-site";

export const PREFERRED_SITE_COOKIE = "cms_site";

export type SiteRef = { code: string; isPrimary: boolean; domain?: string };

export const SITE_CODE_PATTERN = /^[a-z]{2,3}(?:-[a-z0-9]{2,8})?$/;

const MODULE_PATH_SEGMENTS = [
  "article",
  "doc",
  "video",
  "shop",
  "page",
  "skill",
  "agent",
  "workflow",
  "mcp",
  "prompt",
];

export const RESERVED_PATH_SEGMENTS: readonly string[] = [
  "admin",
  "api",
  "install",
  "join",
  "member",
  "oauth",
  "cashier",
  "external-link",
  "uploads",
  "video-hls",
  "search",
  "share",
  "_next",
  "__not-found__",
  "favicon.ico",
  "robots.txt",
  "sitemap.xml",
  "sitemap.html",
  "sitemap.txt",
  ...MODULE_PATH_SEGMENTS,
];

export function isReservedPathSegment(segment: string): boolean {
  return RESERVED_PATH_SEGMENTS.includes(segment.trim().toLowerCase());
}

export function sitePathPrefix(site: SiteRef): string {
  return site.isPrimary || site.domain ? "" : `/${site.code}`;
}

function protocolOf(mainOrigin: string): string {
  return /^http:/i.test(mainOrigin) ? "http:" : "https:";
}

export function siteOrigin(site: SiteRef, mainOrigin: string, currentDomain?: string): string {
  if (site.domain) {
    return site.domain === currentDomain ? "" : `${protocolOf(mainOrigin)}//${site.domain}`;
  }

  return currentDomain ? mainOrigin : "";
}

export function readRequestHost(
  get: (name: string) => string | null,
): string {
  const forwarded = get("x-forwarded-host")?.split(",")[0];

  return (forwarded || get("host") || "").trim().toLowerCase();
}

export function normalizeHost(host: string): string {
  return host.trim().toLowerCase().replace(/:\d+$/, "");
}

const DOMAIN_PATTERN = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{0,61}[a-z0-9]$/;

export function normalizeSiteDomain(input: string): string | null {
  const host = normalizeHost(
    input
      .trim()
      .replace(/^[a-z]+:\/\//i, "")
      .split(/[/?#]/)[0]!,
  ).replace(/\.$/, "");

  if (!host) {
    return "";
  }

  return DOMAIN_PATTERN.test(host) ? host : null;
}

export function findSiteByHost<T extends SiteRef & { enabled: boolean }>(
  sites: T[],
  host: string,
): T | undefined {
  const normalized = normalizeHost(host);
  return normalized ? sites.find((site) => site.enabled && site.domain === normalized) : undefined;
}

export function isSiteNeutralPath(path: string): boolean {
  return (
    /^\/(?:member|external-link)(?:[/?#]|$)/.test(path) ||
    /^\/(?:api|uploads|video-hls|join|install|oauth|cashier)\//.test(path) ||
    /^\/(?:sitemap\.(?:xml|html|txt)|robots\.txt)(?:[?#]|$)/.test(path)
  );
}

export function withSitePrefix(href: string, prefix: string): string {
  if (!prefix || !href.startsWith("/") || href.startsWith("//")) {
    return href;
  }

  if (isSiteNeutralPath(href)) {
    return href;
  }

  return `${prefix}${href}`;
}

export function withSitePrefixOnce(href: string, prefix: string): string {
  if (prefix && (href === prefix || href.startsWith(`${prefix}/`) || href.startsWith(`${prefix}?`))) {
    return href;
  }

  return withSitePrefix(href, prefix);
}

export function adminLinkHref(href: string, prefix: string): string {
  return withSitePrefixOnce(normalizeInternalUrl(href.trim()), prefix);
}

export function splitFirstSegment(pathname: string): { first: string; rest: string } {
  const match = /^\/([^/?#]+)(.*)$/.exec(pathname);

  if (!match) {
    return { first: "", rest: pathname || "/" };
  }

  return { first: decodeSafe(match[1]!), rest: match[2] || "/" };
}

function decodeSafe(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export const SITE_LANGUAGE_PRESETS = [
  { code: "zh", name: "简体中文", locale: "zh-CN", icon: "/images/site-flags/zh.svg", currency: "CNY", rate: 1 },
  { code: "zh-tw", name: "繁體中文", locale: "zh-TW", icon: "/images/site-flags/zh-tw.svg", currency: "TWD", rate: 4.4 },
  { code: "en", name: "English", locale: "en", icon: "/images/site-flags/en.svg", currency: "USD", rate: 0.14 },
  { code: "ja", name: "日本語", locale: "ja", icon: "/images/site-flags/ja.svg", currency: "JPY", rate: 21 },
  { code: "ko", name: "한국어", locale: "ko", icon: "/images/site-flags/ko.svg", currency: "KRW", rate: 190 },
  { code: "de", name: "Deutsch", locale: "de", icon: "/images/site-flags/de.svg", currency: "EUR", rate: 0.13 },
  { code: "fr", name: "Français", locale: "fr", icon: "/images/site-flags/fr.svg", currency: "EUR", rate: 0.13 },
  { code: "es", name: "Español", locale: "es", icon: "/images/site-flags/es.svg", currency: "EUR", rate: 0.13 },
  { code: "ru", name: "Русский", locale: "ru", icon: "/images/site-flags/ru.svg", currency: "RUB", rate: 12 },
  { code: "pt", name: "Português", locale: "pt-BR", icon: "/images/site-flags/pt.svg", currency: "BRL", rate: 0.78 },
] as const;

export const DEFAULT_SITE_CODES = ["zh", "zh-tw", "en"] as const;

export const UI_LANGUAGES = SITE_LANGUAGE_PRESETS.map((preset) => preset.code);

export type UiLanguage = (typeof SITE_LANGUAGE_PRESETS)[number]["code"];

export function uiLanguageOf(locale: string): UiLanguage {
  const value = locale.trim().toLowerCase();

  if (/^zh-(tw|hk|mo|hant)/.test(value)) {
    return "zh-tw";
  }

  const primary = value.split("-")[0];

  return SITE_LANGUAGE_PRESETS.find((preset) => preset.code === primary)?.code ?? "en";
}

export function formatLocaleOf(language: UiLanguage): string {
  return SITE_LANGUAGE_PRESETS.find((preset) => preset.code === language)?.locale ?? "zh-CN";
}
